import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import { createOrganization } from '../../actions/organization';
import { alertError, alertSuccess } from '../../helpers/errorHandling';
import { useDispatch, useSelector } from 'react-redux';
import InnerLoader from '../../Components/Common/InnerLoader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ModifyOrganization = ({ organization = null, onSave }) => {
  const dispatch = useDispatch();
  const { countries } = useSelector((state) => state?.organization);

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: organization?.name || '',
      description: organization?.description || '',
      address: organization?.address || '',
      contactEmail: organization?.contactEmail || '',
      contactPhone: organization?.contactPhone || '',
      country: organization?.country || '',
      isDisabled: organization?.isDisabled || false,
      isDeleted: organization?.isDeleted || false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      description: Yup.string().required('Description is required'),
      address: Yup.string().required('Address is required'),
      contactEmail: Yup.string()
        .email('Invalid email')
        .required('Email is required'),
      contactPhone: Yup.string().required('Phone number is required'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const inputData = {
          input: {
            ...values,
            organizationId: organization?.organizationId || null,
          },
        };

        const res = await createOrganization(inputData)(dispatch);
        if (res?.organizationId) {
          alertSuccess('Organization updated successfully');
          if (onSave) onSave();
        } else {
          alertError({ message: 'Failed, please try again later' });
        }
      } catch (e) {
        console.log(e);
        alertError('Failed, please try again later');
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (countries?.length > 0 && !values.country) {
      setFieldValue('country', countries[0]?.name || '');
    }
  }, [countries, setFieldValue]);

  return (
    <div className="modify-organization">
      <h5>{organization ? 'Edit Organization' : 'Create Organization'}</h5>
      <Form noValidate onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group controlId="name" className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.name && !!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="description" className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.description && !!errors.description}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group controlId="address" className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.address && !!errors.address}
              />
              <Form.Control.Feedback type="invalid">
                {errors.address}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group controlId="contactEmail" className="mb-3">
              <Form.Label>Contact Email</Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                value={values.contactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.contactEmail && !!errors.contactEmail}
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactEmail}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="contactPhone" className="mb-3">
              <Form.Label>Contact Phone</Form.Label>
              <Form.Control
                type="text"
                name="contactPhone"
                value={values.contactPhone}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.contactPhone && !!errors.contactPhone}
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactPhone}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group controlId="country" className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={values.country}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.country && !!errors.country}
              />
              <Form.Control.Feedback type="invalid">
                {errors.country}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="isDisabled" className="mb-3">
              <Form.Check
                type="checkbox"
                name="isDisabled"
                label="Is Disabled"
                checked={values.isDisabled}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.isDisabled && !!errors.isDisabled}
              />
              <Form.Control.Feedback type="invalid">
                {errors.isDisabled}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="isDeleted" className="mb-3">
              <Form.Check
                type="checkbox"
                name="isDeleted"
                label="Is Deleted"
                checked={values.isDeleted}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.isDeleted && !!errors.isDeleted}
              />
              <Form.Control.Feedback type="invalid">
                {errors.isDeleted}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Button type="submit" className="mt-3" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Form>
      {loading && <InnerLoader />}
      {/* <ToastContainer /> */}
    </div>
  );
};

export default ModifyOrganization;
